import React, { useEffect, useState, useCallback, useMemo } from "react";
import Header from "../components/organisms/Header";
import { IoMdDownload } from "react-icons/io";
import LeadCollectionGraph from "../components/organisms/charts/LeadCollectionGraph";
import BookingAnalyticsGraph from "../components/organisms/charts/BookingAnalyticsGraph";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, subDays } from "date-fns";
import { FiBarChart, FiCalendar, FiLock, FiPhone } from "react-icons/fi";
import CancelledReasonGraph from "../components/organisms/charts/CancelledReasonGraph";
import CancellationsAndRescheduledAreaChart from "../components/organisms/charts/CancellationsAndRescheduledAreaChart";
import DoctorAttendanceGraph from "../components/organisms/charts/DoctorAttendanceGraph";
import { useHospDocData } from "../lib/contexts/HospitalDoctorContext";
import RatingGraph from "../components/organisms/charts/RatingGraph";
import ClickGoogleReviewGraph from "../components/organisms/charts/ClickGoogleReviewGraph";
import DateRangePicker from "../components/molecules/DateRangePicker";
import { getDateBeforeXDays } from "../lib/utils/funcs";
import Loader from "../components/molecules/Loader";
import {
  downloadFullBookingData,
  downloadLeadsData,
  downloadDoctorAttendanceData,
  getLeadsCardsData,
} from "../lib/apis/analytics";
import { useFeatureFlags } from "../lib/contexts/FeatureFlagContext";
import { useNavigate } from "react-router-dom";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import ReactDOM from "react-dom";
import AnalyticsPdfReport from "./AnalyticsPdfReport";
import {
  getBookingAnalytics,
  getLeadsAnalytics,
  getCancelreasonAnalytics,
  getRescheduledAndCancelledAnalytics,
  getGoogleReviewAnalytics,
  getRatingAnalytics,
} from "../lib/apis/analytics";
import { MdOutlineFileDownload } from "react-icons/md";
import { toast } from "react-toastify";
import { useUserData } from "../lib/contexts/UserContext";
import DetailDoctorAttendanceModal from "../components/organisms/modals/DetailDoctorAttendanceModal";
import { TbReportSearch } from "react-icons/tb";
import SearchableDropdown from "../components/atoms/SearchableDropdown";
import DownloadRequestModal from "../components/organisms/modals/DownloadRequestModal";
import BookingsExportCard from "../components/organisms/BookingsExportCard";
import { modalStyles } from "../assets/styles/modalStyles";
import AnalyticsNavigationTabs from "../components/molecules/AnalyticsNavigationTabs";

export default function Analytics() {
  const { analytics } = useFeatureFlags();
  const navigate = useNavigate();

  // Use lazy initialization to avoid creating dates on every render
  const [leadDateRange, setLeadDateRange] = useState(() => ({
    startDate: getDateBeforeXDays(30),
    endDate: getDateBeforeXDays(1),
  }));

  const [doctorAttendanceSelectedYear, setDoctorAttendanceSelectedYear] =
    useState<number>(() => new Date().getFullYear());

  const { hospitalID, hospData } = useHospDocData();
  const { userData } = useUserData();

  const handleLeadStartDateChange = (date: Date | null) => {
    if (date) {
      setLeadDateRange((prev) => ({
        ...prev,
        startDate: date,
      }));
    }
  };

  const { doctorsData } = useHospDocData();

  const isLoading =
    hospitalID === undefined ||
    hospData === undefined ||
    analytics === undefined;

  const [selectedDoctor, setSelectedDoctor] = useState({
    name: "",
    mappingId: "",
  });

  const doctorOptions =
    doctorsData
      ?.filter((doctor) => typeof doctor.mapping_id === "string")
      .map((doctor) => ({
        value: doctor.mapping_id || "",
        label: `Dr.${doctor.full_name || ""}`,
      })) || [];

  useEffect(() => {
    if (doctorsData?.length) {
      const first = doctorsData[0];
      setSelectedDoctor({
        name: `Dr. ${first.full_name}`,
        mappingId: first.mapping_id || "",
      });
    }
  }, [doctorsData]);

  const handleLeadEndDateChange = (date: Date | null) => {
    if (date) {
      setLeadDateRange((prev) => ({
        ...prev,
        endDate: date,
      }));
    }
  };

  const handleBookingAnalyticsDownload = async () => {
    if (!hospitalID) {
      toast.error("Hospital ID not available");
      return;
    }
    const hospitalName = hospData?.hospital_name || "Hospital";

    console.log(
      "Requesting booking analytics download with date range:",
      currentBookingDateRange
    );

    try {
      const response = await downloadFullBookingData({
        hospital_id: hospitalID,
        start_date: currentBookingDateRange.startDate,
        end_date: currentBookingDateRange.endDate,
        doctor_id: userData?.doctor_id,
        hospital_name: hospitalName,
      });
      setDownloadApiResponse(response);
      setShowDownloadRequestModal(true);
    } catch (error: any) {
      console.error("Error requesting booking analytics download:", error);
      toast.error(
        error?.response?.data?.message || "Failed to request booking analytics download"
      );
    }
  };

  const handleDownloadLeads = async () => {
    if (!hospitalID) return;
    const hospitalName = hospData?.hospital_name || "Hospital";
    const params = {
      hospital_id: hospitalID,
      start_date: format(leadDateRange.startDate, "yyyy-MM-dd"),
      end_date: format(leadDateRange.endDate, "yyyy-MM-dd"),
      hospital_name: hospitalName,
    };
    try {
      const response = await downloadLeadsData(params);
      setDownloadApiResponse(response);
      setShowDownloadRequestModal(true);
    } catch (error: any) {
      console.error("Error requesting leads download:", error);
      toast.error(
        error?.response?.data?.message || "Failed to request leads download"
      );
    }
  };

  const handleDownloadDoctorAttendance = async () => {
    if (
      !hospitalID ||
      !doctorAttendanceSelectedYear ||
      !selectedDoctor?.mappingId
    )
      return;
    const hospitalName = hospData?.hospital_name || "Hospital";
    const params = {
      hospital_id: hospitalID,
      doctor_mapping_id: selectedDoctor.mappingId,
      year: doctorAttendanceSelectedYear,
      hospital_name: hospitalName,
    };
    try {
      const response = await downloadDoctorAttendanceData(params);
      setDownloadApiResponse(response);
      setShowDownloadRequestModal(true);
    } catch (error: any) {
      console.error("Error requesting doctor attendance download:", error);
      toast.error(
        error?.response?.data?.message || "Failed to request doctor attendance download"
      );
    }
  };

  const [isGenerating, setIsGenerating] = useState(false);
  const [reportStartDate, setReportStartDate] = useState(() =>
    getDateBeforeXDays(30)
  );
  const [reportEndDate, setReportEndDate] = useState(() =>
    getDateBeforeXDays(1)
  );

  const handleDownloadPdf = async () => {
    setIsGenerating(true);

    try {
      const [booking, funnel, leadcards, cancel, rc, google, rating] =
        await Promise.all([
          getBookingAnalytics({
            hospital_id: hospitalID ?? "",
            start_date: format(reportStartDate, "yyyy-MM-dd"),
            end_date: format(reportEndDate, "yyyy-MM-dd"),
          }),
          getLeadsAnalytics({
            hospital_id: hospitalID ?? "",
            start_date: format(reportStartDate, "yyyy-MM-dd"),
            end_date: format(reportEndDate, "yyyy-MM-dd"),
          }),
          getLeadsCardsData({
            hospital_id: hospitalID ?? "",
            start_date: format(reportStartDate, "yyyy-MM-dd"),
            end_date: format(reportEndDate, "yyyy-MM-dd"),
          }),
          getCancelreasonAnalytics({
            hospital_id: hospitalID ?? "",
            start_date: format(reportStartDate, "yyyy-MM-dd"),
            end_date: format(reportEndDate, "yyyy-MM-dd"),
          }),
          getRescheduledAndCancelledAnalytics({
            hospital_id: hospitalID ?? "",
            start_date: format(reportStartDate, "yyyy-MM-dd"),
            end_date: format(reportEndDate, "yyyy-MM-dd"),
          }),
          getGoogleReviewAnalytics({
            hospital_id: hospitalID ?? "",
            start_date: format(reportStartDate, "yyyy-MM-dd"),
            end_date: format(reportEndDate, "yyyy-MM-dd"),
          }),
          getRatingAnalytics({
            hospital_id: hospitalID ?? "",
            start_date: format(reportStartDate, "yyyy-MM-dd"),
            end_date: format(reportEndDate, "yyyy-MM-dd"),
          }),
        ]);

      const tempDiv = document.createElement("div");
      tempDiv.style.position = "fixed";
      tempDiv.style.top = "-9999px";
      document.body.appendChild(tempDiv);

      ReactDOM.render(
        <AnalyticsPdfReport
          hospitalId={hospitalID ?? ""}
          startDate={format(reportStartDate, "yyyy-MM-dd")}
          endDate={format(reportEndDate, "yyyy-MM-dd")}
          bookingStats={booking}
          funnelStats={funnel}
          cancelStats={cancel}
          rcStats={rc}
          googleStats={google}
          ratingStats={rating}
          hospName={hospData?.hospital_name}
          leadcards={leadcards}
        />,
        tempDiv,
        async () => {
          try {
            // Wait for initial render
            await new Promise((r) => setTimeout(r, 1000));

            // Wait for charts to be rendered by checking for chart containers
            let retries = 0;
            const maxRetries = 10;
            const totalExpectedCharts = 6; // BookingAnalytics, LeadCollection, CancelledReason, CancellationsRescheduled, GoogleReview, Rating

            while (retries < maxRetries) {
              const chartElements = tempDiv.querySelectorAll(
                ".apexcharts-svg, .apexcharts-canvas"
              );

              if (chartElements.length >= totalExpectedCharts) {
                console.log("All charts rendered");
                break;
              }
              await new Promise((r) => setTimeout(r, 800));
              retries++;
            }

            // Additional wait to ensure all charts are fully loaded with data
            await new Promise((r) => setTimeout(r, 1500));

            // Method 1: Reduce canvas quality for smaller images
            const canvas = await html2canvas(tempDiv, {
              scale: 2,
              useCORS: true,
              backgroundColor: "#ffffff",
            });

            // Compress the image before adding to PDF
            const imgData = canvas.toDataURL("image/jpeg", 0.8); // Using JPEG with 80% quality

            // Method 2: Create PDF with compression settings
            const pdf = new jsPDF({
              orientation: "p",
              unit: "pt",
              format: "a4",
              compress: true, // Enable PDF compression
            });

            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const imgWidth = pageWidth;
            const imgHeight = (canvas.height * pageWidth) / canvas.width;

            let heightLeft = imgHeight;
            let position = 0;

            // Add image with compression
            pdf.addImage(
              imgData,
              "JPEG",
              0,
              position,
              imgWidth,
              imgHeight,
              undefined,
              "MEDIUM"
            );
            heightLeft -= pageHeight;

            while (heightLeft > 0) {
              position = heightLeft - imgHeight;
              pdf.addPage();
              pdf.addImage(
                imgData,
                "JPEG",
                0,
                position,
                imgWidth,
                imgHeight,
                undefined,
                "MEDIUM"
              );
              heightLeft -= pageHeight;
            }

            // Standard save with jsPDF compression
            const hospitalName =
              hospData?.hospital_name?.replace(/[\s\W]+/g, "_") || "Hospital";
            pdf.save(`${hospitalName}_Analytics_Report_Compressed.pdf`);
            setShowModal(false);
          } catch (err) {
            console.error("PDF generation error:", err);
            toast.error("Failed to generate PDF. Please try again.");
            setShowModal(false);
          } finally {
            ReactDOM.unmountComponentAtNode(tempDiv);
            document.body.removeChild(tempDiv);
            setIsGenerating(false);
            setShowModal(false);
          }
        }
      );
    } catch (err) {
      console.error("Error fetching analytics data:", err);
      alert("Failed to fetch analytics data. Please try again.");
      setIsGenerating(false);
    }
  };

  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDownloadRequestModal, setShowDownloadRequestModal] = useState(false);
  const [downloadApiResponse, setDownloadApiResponse] = useState<any>(null);
  const [currentBookingDateRange, setCurrentBookingDateRange] = useState(
    () => ({
      startDate: format(subDays(new Date(), 7), "yyyy-MM-dd"),
      endDate: format(subDays(new Date(), 1), "yyyy-MM-dd"),
    })
  );

  // Callback handler for chart components to trigger download modal
  const handleDownloadRequestedFromChart = (response: any) => {
    setDownloadApiResponse(response);
    setShowDownloadRequestModal(true);
  };

  // Memoize callback functions to prevent re-renders
  const handleBookingDateRangeChange = useCallback(
    (startDate: string, endDate: string) => {
      console.log("Booking analytics date range changed:", {
        startDate,
        endDate,
      });
      setCurrentBookingDateRange({ startDate, endDate });
    },
    []
  );

  const handleDoctorAttendanceYearChange = useCallback((date: Date | null) => {
    if (date) {
      setDoctorAttendanceSelectedYear(date.getFullYear());
    }
  }, []);

  const selectedDoctorObj = useMemo(
    () =>
      doctorsData?.find((doc) => doc.mapping_id === selectedDoctor.mappingId),
    [doctorsData, selectedDoctor.mappingId]
  );

  return (
    <>
      {isLoading ? (
        <Loader hospitalId={hospitalID} />
      ) : (
        <div className="w-full h-[100vh] overflow-y-auto side_bar pb-6 relative">
          <Header header value={"Analytics"} />

          {userData?.role === "MasterAdmin" && (
            <button
              className="fixed bottom-8 right-10 z-50 bg-blue-500 hover:bg-blue-700 hover:text-white rounded-full shadow-lg w-14 h-14 flex flex-col items-center justify-center group transition"
              onClick={() => setShowModal(true)}
              aria-label="Download Summary Report"
              style={{ boxShadow: "0 4px 16px rgba(37,99,235,0.15)" }}
            >
              <MdOutlineFileDownload className="w-8 h-8 text-white/90 transition-transform duration-300 group-hover:translate-y-[-1px]" />
              {/* Tooltip */}
              <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 -top-4 -translate-y-full opacity-0 group-hover:opacity-100 transition bg-gray-900 text-white text-xs rounded px-3 py-1 whitespace-nowrap z-50 shadow-lg">
                Summary Report
              </span>
            </button>
          )}

          {showModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
              <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md relative">
                <button
                  className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 text-2xl"
                  onClick={() => setShowModal(false)}
                >
                  &times;
                </button>
                <h2 className="text-xl font-semibold mb-6 text-gray-800">
                  Download Summary Report
                </h2>
                <div className="flex flex-col gap-4 mb-6">
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={format(reportStartDate, "yyyy-MM-dd")}
                      onChange={(e) =>
                        setReportStartDate(new Date(e.target.value))
                      }
                      max={format(reportEndDate, "yyyy-MM-dd")}
                      className="mt-1 border border-gray-300 rounded-md px-3 py-2 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={format(reportEndDate, "yyyy-MM-dd")}
                      onChange={(e) =>
                        setReportEndDate(new Date(e.target.value))
                      }
                      min={format(reportStartDate, "yyyy-MM-dd")}
                      max={format(getDateBeforeXDays(0), "yyyy-MM-dd")}
                      className="mt-1 border border-gray-300 rounded-md px-3 py-2 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <button
                  onClick={handleDownloadPdf}
                  disabled={isGenerating}
                  className={`w-full flex items-center justify-center px-5 py-3 font-semibold rounded-md transition bg-blue-600 hover:bg-blue-700 text-white`}
                >
                  {isGenerating ? (
                    <>
                      <svg
                        className="animate-spin h-5 w-5 mr-2 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                        ></path>
                      </svg>
                      Generating PDF...
                    </>
                  ) : (
                    <>
                      <MdOutlineFileDownload className="w-5 h-5 mr-2" />
                      Download PDF
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          <div className="p-7 flex flex-col gap-14">
            <AnalyticsNavigationTabs hospitalId={hospitalID} className="mb-2" />
            <section>
              <div className="border border-[1px] border-r-0 border-l-0 border-t-0 pb-3 border-[#c4c7c5] hidden md:flex flex-row justify-between items-start sm:items-center gap-y-2">
                <div>
                  <h1 className="font-semibold text-xl text-black-700">
                    Appointment Insights
                  </h1>
                  <p className="text-sm text-gray-600 mt-1">
                    Track patient scheduling trends and clinic volume.
                  </p>
                </div>
                <div
                  className="flex gap-2 items-center cursor-pointer p-2 px-4 hover:bg-gray-300 justify-center rounded-md"
                  onClick={handleBookingAnalyticsDownload}
                >
                  <IoMdDownload className="w-6 h-6 text-[#474747]" />
                  <h1 className="font-semibold text-[#474747]">EXPORT</h1>
                </div>
              </div>
              <div className="flex md:hidden border border-[1px] border-r-0 border-l-0 border-t-0 pb-3 border-[#c4c7c5] flex-row justify-between items-start sm:items-center gap-y-2">
                <h1 className="font-semibold text-lg text-[#474747]">
                  Booking Analytics Graph
                </h1>
                <div
                  className="flex gap-2 items-center cursor-pointer p-2 px-4 hover:bg-gray-300 justify-center rounded-md"
                  onClick={handleBookingAnalyticsDownload}
                >
                  <IoMdDownload className="w-5 h-5 text-[#474747]" />
                </div>
              </div>
              <BookingAnalyticsGraph
                onDateRangeChange={handleBookingDateRangeChange}
              />
            </section>

            {userData?.role === "MasterAdmin" && (
              <section>
                <div className="border border-[1px] border-r-0 border-l-0 border-t-0 pb-3 border-[#c4c7c5] flex flex-col md:flex-row justify-between items-start sm:items-center gap-y-2">
                  <div>
                    <h1 className="hidden md:flex font-semibold text-xl text-b;acl-700">
                      Leads Collected via WhatsApp
                    </h1>
                    <p className="text-sm text-gray-600 mt-1">
                      Nurture these potential patients and grow your practice.
                    </p>
                  </div>
                  <div className="flex md:hidden flex-row justify-between w-full">
                    <h1 className="font-semibold text-lg text-[#474747]">
                      Lead Collection
                    </h1>
                    <IoMdDownload
                      className="w-5 h-5 text-[#474747]"
                      onClick={handleDownloadLeads}
                    />
                  </div>
                  <div className="flex gap-4">
                    <DateRangePicker
                      startDate={leadDateRange.startDate}
                      endDate={leadDateRange.endDate}
                      onStartChange={handleLeadStartDateChange}
                      onEndChange={handleLeadEndDateChange}
                      size="md"
                      dateFormat="MMM dd, yyyy"
                      inputClassName="border-blue-300 focus:ring-blue-600 text-sm py-1"
                      separator="to"
                      showIcons={false}
                      maxDate={getDateBeforeXDays(1)}
                      minDate={new Date("2023-01-01")}
                    />
                    <div
                      className="hidden md:flex gap-2 items-center cursor-pointer p-2 px-4 hover:bg-gray-300 justify-center rounded-md"
                      onClick={handleDownloadLeads}
                    >
                      <IoMdDownload className="w-6 h-6 text-[#474747]" />
                      <h1 className="font-semibold text-[#474747]">EXPORT</h1>
                    </div>
                  </div>
                </div>
                <LeadCollectionGraph
                  startDate={format(
                    leadDateRange.startDate || getDateBeforeXDays(30),
                    "yyyy-MM-dd"
                  )}
                  endDate={format(
                    leadDateRange.endDate || getDateBeforeXDays(1),
                    "yyyy-MM-dd"
                  )}
                />
              </section>
            )}

            <section>
              <div className="border-[1px] border-r-0 border-l-0 border-t-0 pb-3 border-[#c4c7c5] flex flex-row justify-between items-start sm:items-center gap-y-2">
                <div>
                  <h1 className="font-semibold text-xl text-black-700">
                    Cancellations & Reschedules
                  </h1>
                  <p className="text-sm text-gray-600 mt-1">
                    Understand why patients cancel and track the daily volume of
                    reschedules.
                  </p>
                </div>
              </div>
              <div className="flex flex-col lg:flex-row gap-6 mt-6">
                <CancelledReasonGraph onDownloadRequested={handleDownloadRequestedFromChart} />
                <CancellationsAndRescheduledAreaChart onDownloadRequested={handleDownloadRequestedFromChart} />
              </div>
            </section>

            {userData?.role === "MasterAdmin" && (
              <section>
                <div className="border border-[1px] border-r-0 border-l-0 border-t-0 pb-3 border-[#c4c7c5] flex flex-col md:flex-row justify-between items-start sm:items-center gap-y-2">
                  {/* --- FIX 1: HEADING & SUBHEADING (DESKTOP) --- */}
                  <div className="hidden md:block">
                    <h1 className="font-semibold text-xl text-black-700">
                      Doctor Attendance & Punctuality
                    </h1>
                    <p className="text-sm text-gray-600 mt-1">
                      Session-wise first check-in and last check-out,
                      highlighting late starts, early exits, and missed
                      sessions.
                    </p>
                  </div>

                  {/* --- FIX 1: HEADING & SUBHEADING (MOBILE) --- */}
                  <div className="flex md:hidden flex-row justify-between w-full items-start">
                    <div>
                      <h1 className="font-semibold text-xl text-black-700">
                        Doctor attendance and punctuality
                      </h1>
                      <p className="text-sm text-gray-600 mt-1 max-w-[85%]">
                        Session-wise first check-in and last check-out,
                        highlighting late starts, early exits, and missed
                        sessions.
                      </p>
                    </div>
                    <IoMdDownload
                      className="w-6 h-6 text-[#474747] flex-shrink-0"
                      onClick={handleDownloadDoctorAttendance}
                    />
                  </div>

                  {/* --- FIX 2: CONTROLS LAYOUT & DROPDOWN WIDTH --- */}
                  <div className="flex flex-row flex-wrap items-center gap-x-4 gap-y-2">
                    {/* Filter 1: Doctor Select (with width) */}
                    <div className="flex items-center gap-1">
                      <label
                        htmlFor="doc-select"
                        className="text-sm font-medium text-gray-700 hidden sm:block"
                      >
                        Doctor:
                      </label>
                      {/* Added w-56 to the wrapper div to ensure full name is visible */}
                      <div className="w-56">
                        <SearchableDropdown
                          //id="doc-select"
                          options={doctorOptions}
                          onChange={(option) =>
                            setSelectedDoctor({
                              name: String(option.label),
                              mappingId: String(option.value),
                            })
                          }
                          selectedDoctor={selectedDoctor}
                          doctorsData={doctorsData}
                          placeholder="Select a doctor"
                        />
                      </div>
                    </div>

                    {/* Filter 2: Year Select */}
                    <div className="flex items-center gap-1">
                      <label
                        htmlFor="year-select"
                        className="text-sm font-medium text-gray-700 hidden sm:block"
                      >
                        Year:
                      </label>
                      <div className="relative">
                        <DatePicker
                          id="year-select"
                          selected={new Date(doctorAttendanceSelectedYear, 0)}
                          onChange={handleDoctorAttendanceYearChange}
                          className="pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-[100px]"
                          dateFormat="yyyy"
                          showYearPicker
                          placeholderText="Select year"
                          maxDate={new Date()}
                          minDate={new Date(2023, 0)}
                          calendarClassName="border-none shadow-lg !text-sm !flex !justify-center !text-center"
                        />
                        <FiCalendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                      </div>
                    </div>

                    {/* Action 1: Report Icon */}
                    <div className="relative group">
                      <button
                        className="p-2 rounded-lg hover:bg-gray-300"
                        onClick={() => setShowDetailModal(true)}
                        aria-label="Doctor Details"
                        type="button"
                      >
                        <TbReportSearch className="w-8 h-8 !text-gray-700" />
                      </button>
                      <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 -top-2 -translate-y-full opacity-0 group-hover:opacity-100 transition bg-gray-900 text-white text-xs rounded px-3 py-1 whitespace-nowrap z-50 shadow-lg">
                        Detailed Doctor Attendance
                      </span>
                    </div>

                    {/* Action 2: Export Button */}
                    <div
                      className="hidden md:flex gap-2 items-center cursor-pointer p-2 px-4 hover:bg-gray-300 justify-center rounded-md"
                      onClick={handleDownloadDoctorAttendance}
                    >
                      <IoMdDownload className="w-6 h-6 text-[#474747]" />
                      <h1 className="font-semibold text-[#474747]">EXPORT</h1>
                    </div>
                  </div>
                </div>
                <DoctorAttendanceGraph
                  doctor={selectedDoctor}
                  year={doctorAttendanceSelectedYear}
                />
              </section>
            )}

            {userData?.role === "MasterAdmin" && (
              <section>
                <div className="border-[1px] border-r-0 border-l-0 border-t-0 pb-3 border-[#c4c7c5] flex flex-row justify-between items-start sm:items-center gap-y-2">
                  <div>
                    <h1 className="font-semibold text-xl text-black-700">
                      Patient Ratings & Google Reviews
                    </h1>
                    <p className="text-sm text-gray-600 mt-1">
                      Review your patient satisfaction scores and track monthly
                      Google Reviews.
                    </p>
                  </div>
                </div>
                <div className="flex flex-col lg:flex-row gap-6 mt-6">
                  <ClickGoogleReviewGraph onDownloadRequested={handleDownloadRequestedFromChart} />
                  <RatingGraph
                    startDate={format(getDateBeforeXDays(30), "yyyy-MM-dd")}
                    endDate={format(getDateBeforeXDays(1), "yyyy-MM-dd")}
                    onDownloadRequested={handleDownloadRequestedFromChart}
                  />
                </div>
              </section>
            )}

            {userData?.role === "MasterAdmin" && (
              <section>
                <div className="border-[1px] border-r-0 border-l-0 border-t-0 pb-3 border-[#c4c7c5] flex flex-row justify-between items-start sm:items-center gap-y-2">
                  <div>
                    <h1 className="font-semibold text-xl text-black-700">
                      Bookings TAT
                    </h1>
                    <p className="text-sm text-gray-600 mt-1">
                      Export per-booking turnaround time data as Excel for offline analysis.
                    </p>
                  </div>
                </div>
                <div className="mt-6">
                  <BookingsExportCard />
                </div>
              </section>
            )}
          </div>

          {!analytics && (
            <div className="fixed inset-0 z-50 flex items-center justify-center">
              <div className="absolute inset-0 backdrop-blur-sm bg-black/20"></div>
              <div className="relative bg-white/90 backdrop-blur-md rounded-2xl border border-white/30 shadow-2xl p-8 max-w-md w-full mx-4 text-center">
                <div className="mb-6 flex justify-center">
                  <div className="bg-blue-500/20 backdrop-blur-sm rounded-full p-4 border border-blue-300/30">
                    <FiLock className="w-8 h-8 text-blue-600" />
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  Analytics Premium
                </h2>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Please subscribe for analytics to access this content and
                  unlock detailed insights about your business performance.
                </p>
                <div className="space-y-3 mb-6 text-left">
                  <div className="flex items-center gap-3 text-gray-700">
                    <FiBarChart className="w-5 h-5 text-blue-500" />
                    <span className="text-sm">Detailed booking analytics</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-700">
                    <FiBarChart className="w-5 h-5 text-blue-500" />
                    <span className="text-sm">Lead collection insights</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-700">
                    <FiBarChart className="w-5 h-5 text-blue-500" />
                    <span className="text-sm">Doctor attendance tracking</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-700">
                    <FiBarChart className="w-5 h-5 text-blue-500" />
                    <span className="text-sm">Rating & review analytics</span>
                  </div>
                </div>

                <div className="bg-blue-50/80 backdrop-blur-sm rounded-xl border border-blue-200/30 p-4 mb-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">
                    Get Subscription
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-center gap-2 text-gray-700">
                      <FiPhone className="w-4 h-4 text-blue-500" />
                      <span>Contact Akshay:</span>

                      <a
                        href="tel:+917760915388"
                        className="text-blue-600 font-medium hover:text-blue-700"
                      >
                        +91 77609 15388
                      </a>
                    </div>
                    <div className="flex items-center justify-center gap-2 text-gray-700">
                      <FiPhone className="w-4 h-4 text-blue-500" />
                      <span>Contact Pushkar:</span>
                      <a
                        href="tel:+917022623779"
                        className="text-blue-600 font-medium hover:text-blue-700"
                      >
                        +91 70226 23779
                      </a>
                    </div>
                  </div>
                </div>

                <button
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg disabled:opacity-50"
                  onClick={() =>
                    hospitalID && navigate(`/${hospitalID}/dashboard`)
                  }
                  disabled={!hospitalID}
                >
                  Back to Dashboard
                </button>
                <p className="text-xs text-gray-500 mt-4">
                  Unlock full analytics dashboard with premium subscription
                </p>
              </div>
            </div>
          )}

          {showDetailModal && (
            <DetailDoctorAttendanceModal
              doctor={selectedDoctorObj}
              onClose={() => setShowDetailModal(false)}
            />
          )}

          {showDownloadRequestModal && (
            <DownloadRequestModal
              closeModal={() => setShowDownloadRequestModal(false)}
              modalIsOpen={showDownloadRequestModal}
              customStyles={modalStyles}
              apiResponse={downloadApiResponse}
            />
          )}
        </div>
      )}
    </>
  );
}
